import { UserAvatar } from "@components/Avatar";
import { Logo } from "@components/Logo";
import { useLogout } from "@features/auth";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CreditCardIcon from "@mui/icons-material/CreditCardOutlined";
import DeveloperBoardIcon from "@mui/icons-material/DeveloperBoardOutlined";
import HistoryIcon from "@mui/icons-material/HistoryRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ListAltIcon from "@mui/icons-material/ListAltRounded";
import LogoutIcon from "@mui/icons-material/LogoutOutlined";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import {
  Box,
  ButtonBase,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAuthStore } from "@stores/authStore";
import { useChatStore } from "@stores/chatStore";
import { useLayoutStore } from "@stores/layoutStore";
import { useMatch, useMatchRoute, useNavigate } from "@tanstack/react-router";
import { type ElementType, type MouseEvent, useState } from "react";

interface RailItem {
  label: string;
  icon: ElementType;
  active: boolean;
  onClick: () => void;
}

/**
 * Compact rail variant of the sidebar — icon-only nav, ~64px wide.
 *
 * Renders three context-aware nav slots:
 *   - Global (no project / system): Projects · Chat · History
 *   - Project: Overview · Chat · BOM · Graph · Versions
 *   - System:  Overview · Topology · Chat
 *
 * The pin-state cluster sits at the bottom: an AUTO/PINNED pill shows
 * the current state and the single Pin toggle right below it sets it.
 *
 * Chat history toggling is intentionally NOT here — the chat-history
 * affordance lives on the chat surface itself (header toolbar) so the
 * rail's hover-peek overlay can never cover it.
 */
export function SidebarRail() {
  const navigate = useNavigate();
  const matchRoute = useMatchRoute();
  const isStreaming = useChatStore((s) => s.isStreaming);
  const setAppNavPin = useLayoutStore((s) => s.setAppNavPin);
  const appNavPin = useLayoutStore((s) => s.appNavPin);

  const projectMatch = useMatch({
    from: "/app/projects/$projectId",
    shouldThrow: false,
  });
  const projectId = projectMatch?.params.projectId;

  const items: RailItem[] = (() => {
    if (projectId) {
      return [
        {
          label: "Overview",
          icon: InfoOutlinedIcon,
          active: !!matchRoute({
            to: "/projects/$projectId/overview",
            params: { projectId },
          }),
          onClick: () =>
            void navigate({
              to: "/projects/$projectId/overview",
              params: { projectId },
            }),
        },
        {
          label: "Chat",
          icon: ChatBubbleOutlineIcon,
          active: !!matchRoute({
            to: "/projects/$projectId/chat",
            params: { projectId },
          }),
          onClick: () =>
            void navigate({
              to: "/projects/$projectId/chat",
              params: { projectId },
            }),
        },
        {
          label: "BOM",
          icon: ListAltIcon,
          active: !!matchRoute({
            to: "/projects/$projectId/bom",
            params: { projectId },
          }),
          onClick: () =>
            void navigate({
              to: "/projects/$projectId/bom",
              params: { projectId },
            }),
        },
        {
          label: "Versions",
          icon: HistoryIcon,
          active: !!matchRoute({
            to: "/projects/$projectId/versions",
            params: { projectId },
          }),
          onClick: () =>
            void navigate({
              to: "/projects/$projectId/versions",
              params: { projectId },
            }),
        },
      ];
    }
    return [
      {
        label: "Projects",
        icon: DeveloperBoardIcon,
        active: !!matchRoute({ to: "/projects" }),
        onClick: () => void navigate({ to: "/projects" }),
      },
      {
        label: "Chat",
        icon: ChatBubbleOutlineIcon,
        active: !!matchRoute({ to: "/chat" }),
        onClick: () => void navigate({ to: "/chat" }),
      },
    ];
  })();

  const isPinned = appNavPin === "expanded";
  const stateLabel = isPinned ? "PINNED" : "AUTO";
  const pinTooltip = isPinned
    ? "Unpin sidebar (auto-collapse on chat)"
    : "Pin sidebar open";

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        py: 1.25,
        px: 0.5,
        gap: 0.25,
      }}
    >
      {/* Brand mark — favicon tier; the graticule is auto-suppressed
          below 22px by the Logo component itself. Wrapped as a route-
          home affordance: click → /projects (workspace index). */}
      <Tooltip title="Go to projects" placement="right" enterDelay={400}>
        <ButtonBase
          onClick={() => void navigate({ to: "/projects" })}
          aria-label="Go to projects"
          focusRipple
          sx={(t) => ({
            mt: 0.5,
            mb: 1,
            p: 0.5,
            borderRadius: 1,
            transition: `background-color ${t.motion.fast}, opacity ${t.motion.fast}`,
            "&:hover": { backgroundColor: t.palette.surface.raised },
            "&:active": { opacity: 0.85 },
            "&.Mui-focusVisible": {
              outline: `2px solid ${t.palette.editorial.main}`,
              outlineOffset: 2,
            },
          })}
        >
          <Logo size={28} pulseEmber={isStreaming} showWordmark={false} />
        </ButtonBase>
      </Tooltip>

      <Divider
        flexItem
        sx={{ mx: 1, mb: 0.75, borderColor: "border.default" }}
      />

      {/* Icon-only nav stack */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0.25,
          width: "100%",
        }}
      >
        {items.map((it) => (
          <RailItemButton key={it.label} item={it} />
        ))}
      </Box>

      <Box sx={{ flexGrow: 1 }} />

      {/* State + pin cluster */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0.5,
          pb: 0.25,
        }}
      >
        <Tooltip
          title={
            isPinned
              ? "Sidebar pinned — stays expanded on every surface"
              : "Auto — collapses on chat surfaces, expanded elsewhere"
          }
          placement="right"
        >
          <Box
            aria-hidden
            sx={{
              writingMode: "vertical-rl",
              transform: "rotate(180deg)",
              fontFamily: (t) => t.fonts.mono,
              fontSize: "0.55rem",
              fontWeight: 600,
              letterSpacing: "0.18em",
              color: isPinned ? "primary.light" : "text.disabled",
              userSelect: "none",
              py: 0.25,
            }}
          >
            {stateLabel}
          </Box>
        </Tooltip>
        <Tooltip title={pinTooltip} placement="right">
          <IconButton
            size="small"
            onClick={() => setAppNavPin(isPinned ? "auto" : "expanded")}
            aria-label={pinTooltip}
            aria-pressed={isPinned}
            sx={{
              color: isPinned ? "primary.light" : "text.secondary",
              transition: (t) => `color ${t.motion.fast}`,
            }}
          >
            {isPinned ? (
              <PushPinIcon sx={{ fontSize: 18 }} />
            ) : (
              <PushPinOutlinedIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </Tooltip>
        <Divider
          flexItem
          sx={{ mx: 1, my: 0.5, borderColor: "border.default" }}
        />
        <UserMenuButton />
      </Box>
    </Box>
  );
}

function RailItemButton({ item }: { item: RailItem }) {
  const { icon: Icon, active, onClick, label } = item;
  return (
    <Tooltip title={label} placement="right">
      <IconButton
        onClick={onClick}
        aria-label={label}
        aria-current={active ? "page" : undefined}
        size="small"
        sx={{
          width: 38,
          height: 38,
          borderRadius: 1.5,
          color: active ? "primary.light" : "text.secondary",
          bgcolor: active ? "accent.soft" : "transparent",
          border: "1px solid",
          borderColor: active ? "border.accent" : "transparent",
          transition: (t) =>
            `background-color ${t.motion.fast}, color ${t.motion.fast}, border-color ${t.motion.fast}, transform ${t.motion.fast}`,
          "&:hover": {
            color: active ? "primary.light" : "text.primary",
            bgcolor: active ? "accent.soft" : "surface.raised",
          },
          "&:active": { transform: "scale(0.92)" },
        }}
      >
        <Icon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

function UserMenuButton() {
  const user = useAuthStore((s) => s.user);
  const logout = useLogout();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  if (!user) return null;

  const handleOpen = (e: MouseEvent<HTMLElement>) => setAnchor(e.currentTarget);
  const handleClose = () => setAnchor(null);
  const displayName = user.name || user.email;

  return (
    <>
      <Tooltip title={displayName} placement="right">
        <IconButton
          onClick={handleOpen}
          aria-label="Open user menu"
          size="small"
          sx={{ p: 0.25 }}
        >
          <UserAvatar name={user.name} picture={user.picture} size={28} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{ paper: { sx: { minWidth: 220, ml: 0.5 } } }}
      >
        <Box sx={{ px: 1.5, py: 1 }}>
          <Typography
            sx={{
              fontSize: "0.8125rem",
              color: "text.primary",
              fontWeight: 600,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {displayName}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.7rem",
              color: "text.secondary",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {user.email}
          </Typography>
        </Box>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem
          onClick={() => {
            handleClose();
            void navigate({ to: "/settings/billing" });
          }}
        >
          <ListItemIcon>
            <CreditCardIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Billing" />
        </MenuItem>
        {user.is_admin && (
          <MenuItem
            onClick={() => {
              handleClose();
              void navigate({ to: "/admin/users" });
            }}
          >
            <ListItemIcon>
              <AdminPanelSettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Admin" />
          </MenuItem>
        )}
        <Divider sx={{ my: 0.5 }} />
        <MenuItem
          onClick={() => {
            handleClose();
            logout.mutate();
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Sign out" />
        </MenuItem>
      </Menu>
    </>
  );
}
